import {
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import { UserContext } from '../../../../context';
import { EventUserStatus, useLazyEventParticipantsQuery, UserPreview } from '../../../../services';
import { useDebounce } from '../../../hooks';
import { getChannelDomain, getWebAppUrl, layoutPath } from '../../../utils';
import { InputField } from '../../InputField';
import { UserDisplayItem } from '../../UserDisplayItem';

import classes from './EventParticipantsList.module.scss';

interface EventParticipantsListProps {
  eventId: number;
  status: EventUserStatus;
  scrollableTarget?: string;
}

export const EventParticipantsList: FunctionComponent<EventParticipantsListProps> = memo(
  ({ eventId, status, scrollableTarget }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'events' });

    const [eventParticipants, { data, isFetching }] = useLazyEventParticipantsQuery();

    const userId = useContext(UserContext).userProfile.id;

    const [participants, setParticipants] = useState<UserPreview[]>([]);

    const [searchQuery, setSearchQuery] = useState('');

    const pageRef = useRef(1);

    const debouncedQuery = useDebounce(searchQuery);

    const { items = [], pageInfo = { page: 1, hasNextPage: false } } = { ...data };

    const showSearch = useMemo(
      () => participants.length >= 2 || pageInfo.hasNextPage || Boolean(searchQuery),
      [pageInfo.hasNextPage, participants.length, searchQuery]
    );

    const onParticipantClick = useCallback(
      (id: number) => {
        const url = `${getWebAppUrl(getChannelDomain())}${layoutPath(
          id === userId ? '/profile' : `/profile/${id}`
        )}`;
        window.open(url, '_blank', 'noopener, noreferrer');
      },
      [userId]
    );

    const getEventParticipants = useCallback(
      (page: number) => {
        eventParticipants({
          eventId,
          filters: { participantStatuses: [status], query: debouncedQuery },
          page,
        });
      },
      [debouncedQuery, eventId, eventParticipants, status]
    );

    useEffect(() => {
      pageRef.current = 1;
      getEventParticipants(1);
    }, [getEventParticipants]);

    useEffect(() => {
      if (!data) {
        return;
      }

      setParticipants((participantsPrev) =>
        pageRef.current === 1 ? items : [...participantsPrev, ...items]
      );
    }, [data, items]);

    const loadMore = useCallback(() => {
      pageRef.current += 1;
      getEventParticipants(pageRef.current);
    }, [getEventParticipants]);

    const renderParticipants = useMemo(() => {
      if (isFetching && pageRef.current === 1) {
        return <Skeleton height={'3rem'} />;
      }

      if (!isFetching && participants.length === 0) {
        return <div className={classes['empty']}>{ctxT('no-users')}</div>;
      }

      return (
        <InfiniteScroll
          next={loadMore}
          hasMore={pageInfo.hasNextPage}
          dataLength={participants.length}
          loader={<Skeleton height={'3rem'} />}
          className={classes['list']}
          scrollableTarget={scrollableTarget}
        >
          {participants.map(({ id, avatar, screenName }) => (
            <UserDisplayItem
              key={id}
              screenName={screenName}
              avatar={avatar}
              onClick={() => onParticipantClick(id)}
              className={classes['user']}
              hover
            />
          ))}
        </InfiniteScroll>
      );
    }, [
      ctxT,
      isFetching,
      loadMore,
      onParticipantClick,
      pageInfo.hasNextPage,
      participants,
      scrollableTarget,
    ]);

    return (
      <div className={classes['container']}>
        {showSearch && (
          <InputField
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        )}
        {renderParticipants}
      </div>
    );
  }
);
